import { useState } from "react";
import { Bookmark, RefreshCw, Trash2, Copy, Check, AlertTriangle, ShieldCheck, Ban, Eye, Share2, Flag } from "lucide-react";
import TimelineBar from "./TimelineBar";
import RiskMeter from "./RiskMeter";
import DarkRailsBadge from "./DarkRailsBadge";
import BestChannelStrip from "./BestChannelStrip";

const approvalBadge = (a) => {
    if (a === "approved")
        return { label: "APPROVED", className: "text-[#f5f5f0] border-[#262626] bg-[#121212]", Icon: ShieldCheck };
    if (a === "needs_rewrite")
        return { label: "NEEDS REWRITE", className: "text-[#ffb800] border-[#ffb800] bg-[#ffb80012]", Icon: AlertTriangle };
    return { label: "REJECTED", className: "text-[#ff2a2a] border-[#ff2a2a] bg-[#ff2a2a12]", Icon: Ban };
};

const RollupRow = ({ rollup, clipId }) => {
    if (!rollup || rollup.event_count === 0) {
        return (
            <div
                data-testid={`rollup-${clipId}`}
                className="flex items-center gap-4 px-5 py-2 bg-[#0a0a0a] border-t border-[#262626]"
            >
                <span className="d8-label">Live signal</span>
                <span className="font-mono-d8 text-[10px] text-[#8a8a8a]">no events yet</span>
            </div>
        );
    }
    const fmt = (n) => (n || 0).toLocaleString();
    const reportCls =
        rollup.report_risk === "high" ? "text-[#ff2a2a]" : rollup.report_risk === "elevated" ? "text-[#ffb800]" : "text-[#8a8a8a]";
    return (
        <div
            data-testid={`rollup-${clipId}`}
            className="flex flex-wrap items-center gap-x-5 gap-y-1 px-5 py-2 bg-[#0a0a0a] border-t border-[#262626]"
        >
            <span className="d8-label">Live signal</span>
            <span className="font-mono-d8 text-[11px] text-[#f5f5f0] inline-flex items-center gap-1"><Eye className="w-3 h-3" /> {fmt(rollup.views)}</span>
            <span className="font-mono-d8 text-[11px] text-[#f5f5f0] inline-flex items-center gap-1"><Share2 className="w-3 h-3" /> {fmt(rollup.shares)}</span>
            <span className="font-mono-d8 text-[11px] text-[#f5f5f0]">SAV {fmt(rollup.saves)}</span>
            <span className="font-mono-d8 text-[11px] text-[#f5f5f0]">
                CMP {rollup.completion_rate_avg !== null ? `${Math.round(rollup.completion_rate_avg * 100)}%` : "—"}
            </span>
            <span className={`font-mono-d8 text-[11px] inline-flex items-center gap-1 ${reportCls}`}>
                <Flag className="w-3 h-3" /> {rollup.report_risk}
            </span>
        </div>
    );
};

export const ClipCard = ({ clip, onSave, onDelete, onRewrite, rewriting = false }) => {
    const [copied, setCopied] = useState(false);
    const badge = approvalBadge(clip.overall_approval);

    const handleCopy = async () => {
        const text = `${clip.title}\n\n${clip.script
            .map((s) => `${s.start}-${s.end}  ${s.label.toUpperCase()}\n${s.text}`)
            .join("\n\n")}\n\nCaption: ${clip.caption}\nHashtags: ${(clip.hashtags || []).join(" ")}`;
        await navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
    };

    const s = clip.safety_scores || {};
    const bestFit = clip.dark_rails?.best_fit_platforms || [];

    return (
        <article
            data-testid={`clip-card-${clip.id}`}
            className="bg-[#121212] border border-[#262626] hover:border-[#3a3a3a] transition-colors"
        >
            <div className="flex items-start justify-between gap-3 p-5 border-b border-[#262626]">
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <span className="d8-label">
                            {clip.platform} / {clip.theme} / D{clip.darkness_level}
                        </span>
                    </div>
                    <h3 className="font-heading font-black uppercase tracking-tight text-[#f5f5f0] text-xl leading-none break-words">
                        {clip.title}
                    </h3>
                </div>
                <div className="shrink-0 flex flex-col items-end gap-1.5">
                    <div
                        data-testid={`clip-approval-${clip.id}`}
                        className={`inline-flex items-center gap-1 px-2 py-1 border font-mono-d8 text-[10px] uppercase tracking-widest ${badge.className}`}
                    >
                        <badge.Icon className="w-3 h-3" strokeWidth={2} />
                        {badge.label}
                    </div>
                    <DarkRailsBadge clip={clip} />
                </div>
            </div>

            <div className="px-5 pt-4">
                <TimelineBar testid={`clip-timeline-${clip.id}`} />
            </div>

            <div className="p-5 space-y-3">
                {clip.script.map((blk, i) => (
                    <div
                        key={i}
                        className="grid grid-cols-[92px_1fr] gap-3 border-l-2 pl-3 py-1"
                        style={{ borderColor: i === 0 ? "#f5f5f0" : i === 1 ? "#262626" : "#ff2a2a" }}
                    >
                        <div className="font-mono-d8 text-[11px] text-[#8a8a8a] tabular-nums uppercase tracking-wider">
                            {blk.start}–{blk.end}
                            <div className="text-[9px] mt-0.5">{blk.label}</div>
                        </div>
                        <p className="text-sm text-[#f5f5f0] leading-relaxed">{blk.text}</p>
                    </div>
                ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[#262626] border-y border-[#262626]">
                <div className="bg-[#0a0a0a] p-4">
                    <div className="d8-label mb-1">Visual</div>
                    <p className="text-xs text-[#f5f5f0]">{clip.visual_direction || "—"}</p>
                </div>
                <div className="bg-[#0a0a0a] p-4">
                    <div className="d8-label mb-1">Voiceover</div>
                    <p className="text-xs text-[#f5f5f0]">{clip.voiceover_direction || "—"}</p>
                </div>
            </div>

            <div className="p-5 border-b border-[#262626] space-y-2">
                {clip.caption && (
                    <p className="font-mono-d8 text-xs text-[#f5f5f0]">"{clip.caption}"</p>
                )}
                <div className="flex flex-wrap gap-1.5">
                    {(clip.hashtags || []).map((h, i) => (
                        <span key={i} className="font-mono-d8 text-[10px] uppercase tracking-wider text-[#8a8a8a] border border-[#262626] px-1.5 py-0.5">
                            {h}
                        </span>
                    ))}
                </div>
            </div>

            {/* Dark Rails explanation */}
            {clip.dark_rails && (clip.dark_rails.explanation || bestFit.length > 0 || (clip.dark_rails.platform_dislikes || []).length > 0) && (
                <div
                    data-testid={`dark-rails-explain-${clip.id}`}
                    className="p-5 border-b border-[#262626] bg-[#0a0a0a] space-y-2"
                >
                    <div className="d8-label">Dark Rails</div>
                    {clip.dark_rails.explanation && (
                        <p className="text-xs text-[#f5f5f0] leading-relaxed">{clip.dark_rails.explanation}</p>
                    )}
                    {(clip.dark_rails.platform_dislikes || []).length > 0 && (
                        <div className="flex flex-wrap gap-1.5">
                            {clip.dark_rails.platform_dislikes.map((d, i) => (
                                <span key={i} className="font-mono-d8 text-[10px] uppercase tracking-widest text-[#ff2a2a] border border-[#ff2a2a] px-1.5 py-0.5">
                                    {d}
                                </span>
                            ))}
                        </div>
                    )}
                    {clip.dark_rails.rewrite_angle && (
                        <p className="font-mono-d8 text-[11px] text-[#ffb800] border-l-2 border-[#ffb800] pl-2">
                            rewrite: {clip.dark_rails.rewrite_angle}
                        </p>
                    )}
                    {bestFit.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 pt-1">
                            <span className="d8-label">Best-fit</span>
                            {bestFit.map((p) => (
                                <span key={p} className="font-mono-d8 text-[10px] uppercase tracking-widest text-[#f5f5f0] border border-[#262626] px-1.5 py-0.5">
                                    {p}
                                </span>
                            ))}
                        </div>
                    )}
                </div>
            )}

            <div className="p-5 space-y-3 bg-[#0a0a0a]">
                <div className="flex items-center justify-between">
                    <div className="d8-label">Safety Matrix</div>
                    {clip.rewrite_recommendation && (
                        <span className="d8-label text-[#ffb800]">REWRITE SUGGESTED</span>
                    )}
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    <RiskMeter label="Hook" value={s.hook} />
                    <RiskMeter label="Twist" value={s.twist} />
                    <RiskMeter label="Discomfort" value={s.discomfort} />
                    <RiskMeter label="Replay" value={s.replay} />
                    <RiskMeter label="Platform Risk" value={s.platform_risk} kind="risk" />
                    <RiskMeter label="Harm Risk" value={s.harm_risk} kind="risk" />
                </div>
                {clip.rewrite_recommendation && (
                    <p className="font-mono-d8 text-[11px] text-[#ffb800] border-l-2 border-[#ffb800] pl-2">
                        {clip.rewrite_recommendation}
                    </p>
                )}
            </div>

            <RollupRow rollup={clip.analytics_rollup} clipId={clip.id} />

            <BestChannelStrip clipId={clip.id} />

            <div className="flex items-center gap-px bg-[#262626]">
                <button
                    data-testid={`clip-save-${clip.id}`}
                    onClick={() => onSave && onSave(clip)}
                    className={`flex-1 flex items-center justify-center gap-2 py-3 font-mono-d8 text-[11px] uppercase tracking-widest transition-colors ${
                        clip.saved ? "bg-[#ff2a2a] text-[#f5f5f0] hover:bg-[#cc1a1a]" : "bg-[#121212] text-[#f5f5f0] hover:bg-[#1a1a1a]"
                    }`}
                >
                    <Bookmark className="w-3.5 h-3.5" strokeWidth={2} />
                    {clip.saved ? "Saved" : "Save"}
                </button>
                <button
                    data-testid={`clip-rewrite-${clip.id}`}
                    onClick={() => onRewrite && onRewrite(clip)}
                    disabled={rewriting}
                    className="flex-1 flex items-center justify-center gap-2 py-3 bg-[#121212] text-[#f5f5f0] hover:bg-[#1a1a1a] font-mono-d8 text-[11px] uppercase tracking-widest transition-colors disabled:opacity-50"
                >
                    <RefreshCw className={`w-3.5 h-3.5 ${rewriting ? "animate-spin" : ""}`} strokeWidth={2} />
                    {rewriting ? "Rewriting" : "Rewrite"}
                </button>
                <button
                    data-testid={`clip-copy-${clip.id}`}
                    onClick={handleCopy}
                    className="flex-1 flex items-center justify-center gap-2 py-3 bg-[#121212] text-[#f5f5f0] hover:bg-[#1a1a1a] font-mono-d8 text-[11px] uppercase tracking-widest transition-colors"
                >
                    {copied ? <Check className="w-3.5 h-3.5" strokeWidth={2} /> : <Copy className="w-3.5 h-3.5" strokeWidth={2} />}
                    {copied ? "Copied" : "Copy"}
                </button>
                {onDelete && (
                    <button
                        data-testid={`clip-delete-${clip.id}`}
                        onClick={() => onDelete(clip)}
                        className="flex items-center justify-center px-4 py-3 bg-[#121212] text-[#8a8a8a] hover:bg-[#ff2a2a] hover:text-[#f5f5f0] transition-colors"
                    >
                        <Trash2 className="w-3.5 h-3.5" strokeWidth={2} />
                    </button>
                )}
            </div>
        </article>
    );
};

export default ClipCard;
